/**
 * Whether a teacher model accepts OpenAI ``response_format: json_schema``.
 *
 * Mirrors ``supports_json_schema_response_format`` in
 * ``optune-utils/.../param_support.py``. Keep the denylist in sync:
 * classic gpt-4 / gpt-4-* snapshots and gpt-3.5* reject Structured
 * Outputs; gpt-4o / gpt-4.1 / gpt-4.5 / gpt-5 / o* / chatgpt-4o* accept
 * them. Non-OpenAI adapters consume the schema through other paths and
 * always show the field.
 */

import { getJsonSchemaError } from './inferJsonSchema';

export const SCHEMA_ENABLED_EMPTY_MESSAGE =
    'JSON schema is enabled. Enter a valid schema or turn it off.';

const OPENAI_FAMILY_PREFIXES = new Set(['', 'openai', 'azure', 'azure_ai']);

export function canonicalOpenAIModelName(modelId, modelName) {
    const raw = String(modelName || modelId || '').trim();
    const leaf = raw.includes('/') ? raw.slice(raw.lastIndexOf('/') + 1) : raw;
    return leaf.toLowerCase();
}

export function openaiFamilySupportsJsonSchema(canonical) {
    if (!canonical) return true;
    if (canonical.startsWith('gpt-3.5')) return false;
    if (canonical === 'gpt-4' || canonical.startsWith('gpt-4-')) return false;
    return true;
}

export function supportsJsonSchemaResponseFormat({
    modelId,
    prefix = '',
    modelName,
} = {}) {
    if (!OPENAI_FAMILY_PREFIXES.has(prefix || '')) return true;
    return openaiFamilySupportsJsonSchema(
        canonicalOpenAIModelName(modelId, modelName)
    );
}

/**
 * Model Setup visibility for the teacher JSON schema field.
 *
 * Prefers the catalog flag from ``GET /available-models``; falls back to
 * the denylist so a stale catalog still hides gpt-4.
 */
export function teacherModelSupportsJsonSchema({
    provider,
    modelId,
    availableProviders = {},
} = {}) {
    const models = availableProviders?.[provider]?.models || [];
    const row = models.find((m) => m?.id === modelId);
    if (row && typeof row.supports_json_schema === 'boolean') {
        return row.supports_json_schema;
    }
    return supportsJsonSchemaResponseFormat({
        modelId,
        prefix: provider || '',
        modelName: row?.model_name,
    });
}

/**
 * Resolve the teacher schema for a live request (Run / Infer).
 *
 * Toggle-off / empty → no schema. Toggle-on with empty or invalid text →
 * ``error`` so the caller can refuse to run unconstrained. Unsupported
 * models hide the field and never send leftover text.
 *
 * @returns {{schema: string|null, error: string|null}}
 */
export function resolveTeacherSchemaForRequest({
    schema,
    provider,
    modelId,
    availableProviders = {},
    schemaEnabled = false,
} = {}) {
    if (!teacherModelSupportsJsonSchema({
        provider,
        modelId,
        availableProviders,
    })) {
        return { schema: null, error: null };
    }
    const error = getJsonSchemaError(schema);
    if (error) {
        return { schema: null, error };
    }
    const trimmed = (schema || '').trim();
    if (schemaEnabled && !trimmed) {
        return { schema: null, error: SCHEMA_ENABLED_EMPTY_MESSAGE };
    }
    return { schema: trimmed || null, error: null };
}

/**
 * Schema string to send on a teacher request. ``null`` when the model
 * cannot use ``json_schema``, the field is empty, or the text is invalid
 * (callers that must fail instead of dropping use
 * ``resolveTeacherSchemaForRequest``).
 */
export function teacherSchemaForRequest(args = {}) {
    return resolveTeacherSchemaForRequest(args).schema;
}

/**
 * Teacher schema value used when the student inherits (Model Setup / re-eval).
 * Unsupported teacher models hide the field; leftover saved schema must not
 * appear as the inherited student default.
 */
export function effectiveTeacherSchemaForStudent({
    teacherSchema,
    provider,
    modelId,
    availableProviders = {},
} = {}) {
    if (!teacherModelSupportsJsonSchema({
        provider,
        modelId,
        availableProviders,
    })) {
        return '';
    }
    return (teacherSchema || '').trim();
}
